import type { FieldDef } from '../../components/CrudSection';
import { enumOptions } from '../../lib/enums';

/**
 * What a taxi driver or tour agency is, declared once.
 *
 * Two screens ask for these: the form that adds somebody, and the panel that changes them on their
 * own page. Written out twice they drift — and the way they drift here is expensive, because half
 * these fields are the difference between a transfer that goes through and one that bounces, and
 * one of them is money stored in minor units.
 */

export const PROFILES_PATH = '/admin/v1/referrals/profiles';

export interface ReferralPartner {
  id: string;
  name: string;
  type: string;
  parentId?: string | null;
  preferredPayoutType: string;
  commissionPlanId?: string | null;
  payoutCadence?: string | null;
  bankName?: string | null;
  accountType?: string | null;
  accountNumber?: string | null;
  accountCurrency?: string | null;
  accountHolderName?: string | null;
  bankDetailsVerified?: boolean;
  idDocumentType?: string | null;
  idDocumentNumber?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  payoutThresholds?: { payoutType: string; thresholdMinor: number; currency: string }[] | null;
  notes?: string | null;
  active?: boolean;
}

export const PARTNER_FIELDS: FieldDef[] = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  {
    name: 'type',
    label: 'Driver, agency or working on their own',
    type: 'select',
    required: true,
    options: enumOptions('referralPartyKind'),
  },
  {
    name: 'parentId',
    label: 'Agency they drive for',
    type: 'entity',
    resource: 'referralProfile',
    description: 'Leave empty for an agency or someone working on their own',
    nullable: true,
    // The ledger refuses a driver with no agency, because tier bonuses are worked out
    // across an agency's drivers and a loose driver would earn nobody anything. Asked
    // here rather than found out on save, where the answer came back as "check the
    // highlighted fields" with nothing highlighted.
    validate: (value, values) =>
      values.type === 'driver' && !value
        ? 'A driver has to be under an agency. Choose "Independent" above for someone working on their own.'
        : null,
  },
  {
    name: 'idDocumentType',
    label: 'Kind of ID',
    type: 'select',
    options: enumOptions('idDocumentType'),
    nullable: true,
  },
  { name: 'idDocumentNumber', label: 'ID number', type: 'text', nullable: true },
  { name: 'phone', label: 'Phone', type: 'phone', nullable: true },
  { name: 'whatsapp', label: 'WhatsApp', type: 'phone', nullable: true },
  { name: 'email', label: 'Email', type: 'text', nullable: true },
  {
    /*
     * HOW OFTEN THEY ARE SETTLED WITH — and this is the field on this form that moves money.
     *
     * The owner: a partner "might be paid, weekly, semi-monthly or monthly", per person. It reads
     * like a preference and it is not one: the payout period is the window a volume threshold is
     * counted inside, so crossing a threshold applies the better rate to everything in the period.
     * Changing this redraws the period a partner is in RIGHT NOW, and his unpaid commission is
     * worked out again over the new stretch. Anything already paid is frozen and never restated.
     *
     * Left empty is a real state and not a blank to be filled in: nobody has agreed one, and the
     * count stays what the bar has always used — every bill nobody has been paid for. There is no
     * backfill on the column for exactly that reason.
     *
     * NOTHING IS SCHEDULED OFF IT. No job pays anybody when a period ends, nothing chases and
     * nothing sends. The figures are pulled by hand, whenever the owner wants them.
     *
     * NOT `nullable`, and that is the one surprising line here. A nullable field posts an explicit
     * null when its box is empty, so that clearing something is never mistaken for leaving it out —
     * right for a phone number, wrong for this. Emptying the box would mean "he was never asked",
     * which is a state with a different tier window behind it and unpaid money riding on it, and
     * the server refuses to put anybody back into it from either door. Left out instead, so an
     * untouched box says nothing rather than saying something the server has to ignore. The last
     * line of the description is the box telling the reader the same thing.
     */
    name: 'payoutCadence',
    label: 'How often they are paid',
    type: 'select',
    options: enumOptions('payoutCadence'),
    description:
      'Also the period their volume counts in: if they cross a higher rate, it applies to everything in the period, not just what comes after. Changing it works their unpaid commission out again over the new period; anything already paid does not move. Leave it empty until you have agreed one — once you have, it can be changed but not emptied.',
  },
  {
    name: 'preferredPayoutType',
    label: 'How they want to be paid',
    type: 'select',
    required: true,
    options: enumOptions('payoutMethod'),
    description: 'Cash can be handed over any time; a transfer waits for the minimum below',
  },
  { name: 'bankName', label: 'Bank', type: 'text', nullable: true },
  {
    name: 'accountType',
    label: 'Account type',
    type: 'select',
    options: enumOptions('bankAccountType'),
    nullable: true,
  },
  {
    name: 'accountNumber',
    label: 'Account number',
    type: 'text',
    nullable: true,
    // Nobody can be paid by transfer without one, and the ledger says so on save. Said
    // here instead, next to the box that fixes it.
    validate: (value, values) =>
      values.preferredPayoutType === 'transfer' && !value
        ? 'Needed before anyone can be paid by transfer.'
        : null,
  },
  {
    name: 'accountCurrency',
    label: 'Account currency',
    type: 'select',
    options: enumOptions('currency'),
    nullable: true,
  },
  {
    name: 'accountHolderName',
    label: 'Name on the account',
    type: 'text',
    description: 'Only if the account is not in their own name',
    nullable: true,
  },
  {
    name: 'payoutThresholds',
    label: 'Minimum before they get paid',
    type: 'rows',
    rowNoun: 'minimum',
    description:
      'Commission builds up until it reaches this, so you are not making a bank transfer for L 40. A different minimum can apply to cash than to a transfer.',
    rowFields: [
      {
        name: 'payoutType',
        label: 'Paid by',
        type: 'select',
        defaultValue: 'transfer',
        enumGroup: 'payoutMethod',
      },
      { name: 'thresholdMinor', label: 'Not until it reaches', type: 'number', unit: 'money' },
      {
        name: 'currency',
        label: 'Currency',
        type: 'select',
        defaultValue: 'HNL',
        enumGroup: 'currency',
      },
    ],
  },
  {
    name: 'commissionPlanId',
    label: 'Commission plan',
    type: 'entity',
    resource: 'commissionPlan',
    description: 'Which plan decides what they earn',
    nullable: true,
  },
  { name: 'notes', label: 'Notes', type: 'text', nullable: true },
  {
    name: 'active',
    label: 'Still brings you guests',
    type: 'boolean',
    defaultValue: true,
    description:
      'Turn this off when a driver stops working with you. Their commission history stays; they simply stop appearing on payout lists.',
  },
];
